import React, { useRef, useState } from 'react';
import { Box, Map, Scissors, Ruler, MousePointerClick, Maximize2, Layers, ChevronDown, RotateCcw, Sparkles, X, Loader2, RefreshCw } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Slider } from '../../../components/ui/slider';
import { Scene3D } from './Scene3D';
import { LAYERS, ROOM_LABELS, modelStats } from './model3d';

const TOOLS = [
  { key: '3d', label: '3D', icon: Box, kind: 'mode' },
  { key: 'plan', label: 'Plan', icon: Map, kind: 'mode' },
  { key: 'section', label: 'Sektion', icon: Scissors, kind: 'mode' },
  { key: 'dims', label: 'Mått', icon: Ruler, kind: 'toggle' },
  { key: 'mark', label: 'Markering', icon: MousePointerClick, kind: 'toggle' },
];

const Compass = ({ compassRef }) => (
  <div className="op-round absolute top-16 right-4 w-12 h-12 rounded-full bg-black/40 backdrop-blur border border-white/15 flex items-center justify-center" data-testid="viewer3d-compass">
    <div ref={compassRef} className="relative w-full h-full">
      <span className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white">N</span>
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full w-0 h-0 border-x-[4px] border-x-transparent border-b-[14px] border-b-[#F26B1F]" />
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 w-0 h-0 border-x-[4px] border-x-transparent border-t-[14px] border-t-white/60" />
    </div>
  </div>
);

const Generating = ({ failed, error, onRetry }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 op-grid-bg" data-testid="viewer3d-generating">
    {failed ? (
      <>
        <p className="text-sm text-white/80">{error || '3D-modellen kunde inte skapas.'}</p>
        <button onClick={onRetry} className="op-btn-primary mt-4" data-testid="viewer3d-retry-btn"><RefreshCw size={15} />Försök igen</button>
      </>
    ) : (
      <>
        <div className="relative w-16 h-16 mb-4">
          <div className="absolute inset-0 rounded-2xl bg-[#F26B1F]/25 blur-xl op-glow" />
          <div className="relative w-16 h-16 rounded-2xl bg-[#F26B1F] flex items-center justify-center"><Loader2 className="animate-spin text-white" size={26} /></div>
        </div>
        <p className="text-white font-semibold">AI bygger 3D-modell från ritningen…</p>
        <p className="text-xs text-white/55 mt-1.5 max-w-xs">Läser rum, väggar och mått. Tar oftast 30–90 sekunder.</p>
      </>
    )}
  </div>
);

export const BlueprintViewer3D = ({ model3d, highlight, onClearHighlight, apiRef }) => {
  const { status, model, error, regenerate } = model3d;
  const wrapRef = useRef(null);
  const compassRef = useRef(null);
  const [mode, setMode] = useState('3d');
  const [dims, setDims] = useState(false);
  const [mark, setMark] = useState(false);
  const [show, setShow] = useState(Object.fromEntries(LAYERS.map((l) => [l.key, true])));
  const [floor, setFloor] = useState('all');
  const [sectionPos, setSectionPos] = useState(0.5);
  const [room, setRoom] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const stats = modelStats(model);
  const floorLimit = floor === 'all' ? model?.floors.length || 1 : parseInt(floor, 10);
  const shown = { ...show, roof: show.roof && !mark && mode !== 'plan' && highlight !== 'rooms' && highlight !== 'intWalls' };

  const clickTool = (t) => {
    if (t.kind === 'mode') setMode((m) => (m === t.key && t.key !== '3d' ? '3d' : t.key));
    if (t.key === 'dims') setDims((d) => !d);
    if (t.key === 'mark') { setMark((m) => !m); setRoom(null); }
  };
  const isOn = (t) => (t.kind === 'mode' ? mode === t.key : t.key === 'dims' ? dims : mark);
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : wrapRef.current?.requestFullscreen?.());

  return (
    <div ref={wrapRef} className="relative h-[380px] sm:h-[440px] lg:h-[480px] rounded-2xl overflow-hidden bg-[#141312] border border-black/40 shadow-[0_24px_60px_-30px_rgba(0,0,0,.7)]" data-testid="blueprint-viewer-3d">
      {model && status === 'completed' ? (
        <Scene3D model={model} mode={mode} show={shown} highlight={highlight} floorLimit={floorLimit} sectionPos={sectionPos}
          selectedRoom={room} onSelectRoom={setRoom} showDims={dims} markMode={mark} resetKey={resetKey} compassRef={compassRef} apiRef={apiRef} />
      ) : (
        <Generating failed={status === 'failed'} error={error} onRetry={regenerate} />
      )}

      <div className="absolute left-3 top-3 bottom-3 flex flex-col gap-1.5 z-10" data-testid="viewer3d-toolbar">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          const on = isOn(t);
          return (
            <button key={t.key} onClick={() => clickTool(t)} disabled={!model} data-testid={`viewer3d-tool-${t.key}`}
              className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl flex flex-col items-center justify-center gap-1 text-[10px] font-semibold transition-colors ${on ? 'bg-[#F26B1F] text-white' : 'bg-black/45 backdrop-blur text-white/75 hover:bg-black/65 hover:text-white'} disabled:opacity-40`}>
              <Icon size={17} /><span className="hidden sm:block">{t.label}</span>
            </button>
          );
        })}
        <button onClick={fullscreen} data-testid="viewer3d-tool-fullscreen" className="w-11 sm:w-14 h-11 rounded-xl flex items-center justify-center bg-black/45 backdrop-blur text-white/75 hover:text-white"><Maximize2 size={17} /></button>
      </div>

      {model && (
        <div className="absolute top-3 left-16 sm:left-20 right-3 flex items-center gap-2 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 px-3 rounded-xl bg-black/45 backdrop-blur border border-white/10 text-white text-xs font-semibold inline-flex items-center gap-2" data-testid="viewer3d-layers-btn">
                <Layers size={14} /><span className="hidden sm:inline">Byggnadsdelar</span><ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-52 rounded-xl">
              <DropdownMenuLabel>Visa i modellen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LAYERS.map((l) => (
                <DropdownMenuCheckboxItem key={l.key} checked={show[l.key]} onCheckedChange={(v) => setShow((s) => ({ ...s, [l.key]: !!v }))}
                  onSelect={(e) => e.preventDefault()} data-testid={`viewer3d-layer-${l.key}`}>{l.label}</DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {highlight && (
            <button onClick={onClearHighlight} className="h-9 px-3 rounded-xl bg-[#F26B1F] text-white text-xs font-semibold inline-flex items-center gap-1.5" data-testid="viewer3d-highlight-chip">
              Markerat: {LAYERS.find((l) => l.key === highlight)?.label}<X size={13} />
            </button>
          )}
          <div className="flex-1" />
          <Select value={floor} onValueChange={setFloor}>
            <SelectTrigger className="h-9 w-[120px] sm:w-[150px] rounded-xl bg-black/45 backdrop-blur border-white/10 text-white text-xs font-semibold" data-testid="viewer3d-floor-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla plan</SelectItem>
              {model.floors.map((f) => <SelectItem key={f.level} value={String(f.level)}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {model && <Compass compassRef={compassRef} />}

      {model && mode === 'section' && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 w-[min(320px,60%)] rounded-xl bg-black/55 backdrop-blur px-4 py-3 z-10" data-testid="viewer3d-section-slider">
          <p className="text-[10px] font-semibold text-white/70 mb-2">Snittläge</p>
          <Slider value={[sectionPos * 100]} min={5} max={95} step={1} onValueChange={([v]) => setSectionPos(v / 100)} />
        </div>
      )}

      {model && (
        <div className="absolute bottom-3 left-16 sm:left-20 flex flex-wrap items-center gap-2 z-10">
          <span className="h-8 px-3 rounded-lg bg-black/50 backdrop-blur text-[11px] text-white/80 inline-flex items-center gap-1.5" data-testid="viewer3d-source">
            <Sparkles size={12} className="text-[#F26B1F]" />
            {{ procedural: 'Schematisk modell', analysis: 'AI-modell från ritningsanalys', blueprint: 'AI-modell från ritning' }[model.source] || 'AI-modell'} · {Math.round((model.confidence || 0) * 100)}% säkerhet
          </span>
          <button onClick={() => setResetKey((k) => k + 1)} className="h-8 w-8 rounded-lg bg-black/50 backdrop-blur text-white/80 hover:text-white inline-flex items-center justify-center" data-testid="viewer3d-reset-btn" title="Återställ vy"><RotateCcw size={13} /></button>
          <button onClick={regenerate} className="h-8 px-2.5 rounded-lg bg-black/50 backdrop-blur text-white/80 hover:text-white text-[11px] inline-flex items-center gap-1" data-testid="viewer3d-regenerate-btn"><RefreshCw size={12} />Bygg om</button>
        </div>
      )}

      {room && (
        <div className="absolute bottom-3 right-3 w-56 rounded-xl bg-white p-3.5 shadow-2xl z-10 op-rise" data-testid="viewer3d-room-card">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{room.name}</p>
              <p className="text-[11px] text-stone-500">{room.floorName} · {ROOM_LABELS[room.type] || 'Rum'}</p>
            </div>
            <button onClick={() => setRoom(null)} className="text-stone-400 hover:text-stone-700" data-testid="viewer3d-room-close"><X size={14} /></button>
          </div>
          <div className="mt-2.5 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg bg-stone-100 p-2"><p className="text-stone-500">Yta</p><p className="font-bold op-num">{(room.w * room.d).toFixed(1)} m²</p></div>
            <div className="rounded-lg bg-stone-100 p-2"><p className="text-stone-500">Mått</p><p className="font-bold op-num">{room.w.toFixed(1)} × {room.d.toFixed(1)} m</p></div>
          </div>
        </div>
      )}
      {stats && !room && (
        <div className="hidden sm:block absolute bottom-3 right-3 rounded-xl bg-black/50 backdrop-blur px-3 py-2 text-[11px] text-white/80 z-10 op-num" data-testid="viewer3d-stats">
          {stats.floors} plan · {stats.rooms} rum · {Math.round(stats.bta).toLocaleString('sv-SE')} m² BTA
        </div>
      )}
    </div>
  );
};
